import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
    GetAllSegments,
    GetSegmentCoursesInfo,
    GetSegmentTopicsInfo,
} from "../../../api/CMS";
import ResponseStatus from "../../../util/enums";
import {
    CLASS_TYPE_YKS,
    CLASS_TYPE_LGS,
    EXAM_CATEGORY_ID_BY_CLASS_TYPE,
} from "../constants";

// Classification cascade on the new segment APIs (replaces Book/getnestedtitles):
//   getsegments → ClassType radio (90 YKS / 91 LGS) → ExamType (segment) select
//   getsegmentCoursesInfo?segmentId= → course select
//   getsegmentTopicsInfo?segmentCourseId= → "Konu" (distinct Title) + "Alt başlık" (Name, value=row Id)
// Selecting any level clears every level below it.
export const useSegmentCatalog = (cmsToken) => {
    const [segments, setSegments] = useState([]);
    const [courses, setCourses] = useState([]);
    const [topics, setTopics] = useState([]);
    const [selectedClassTypeId, setSelectedClassTypeId] = useState("");
    const [selectedSegmentId, setSelectedSegmentId] = useState("");
    const [selectedCourseId, setSelectedCourseId] = useState("");
    const [selectedTitle, setSelectedTitle] = useState("");
    const [selectedTopicId, setSelectedTopicId] = useState("");
    const [catalogLoading, setCatalogLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const fetchSegments = async () => {
            setCatalogLoading(true);
            const res = await GetAllSegments(cmsToken);
            if (cancelled) return;
            if (res && res.data && res.data.ResponseStatus === ResponseStatus.Success) {
                const visible = res.data.ResponseData.filter(
                    (s) => s.ClassTypeId === CLASS_TYPE_YKS || s.ClassTypeId === CLASS_TYPE_LGS
                );
                setSegments(visible);
            } else {
                toast.error("Sınav bilgileri alınamadı, lütfen tekrar deneyiniz");
            }
            setCatalogLoading(false);
        };
        fetchSegments();
        return () => {
            cancelled = true;
        };
    }, [cmsToken]);

    const selectClassType = useCallback((classTypeId) => {
        setSelectedClassTypeId(classTypeId);
        setSelectedSegmentId("");
        setCourses([]);
        setSelectedCourseId("");
        setTopics([]);
        setSelectedTitle("");
        setSelectedTopicId("");
    }, []);

    const selectSegment = useCallback(
        async (segmentId) => {
            setSelectedSegmentId(segmentId);
            setCourses([]);
            setSelectedCourseId("");
            setTopics([]);
            setSelectedTitle("");
            setSelectedTopicId("");
            if (!segmentId) return;
            setCatalogLoading(true);
            const res = await GetSegmentCoursesInfo(segmentId, cmsToken);
            if (res && res.data && res.data.ResponseStatus === ResponseStatus.Success) {
                setCourses(res.data.ResponseData);
            } else {
                toast.error("Ders bilgileri alınamadı, lütfen tekrar deneyiniz");
            }
            setCatalogLoading(false);
        },
        [cmsToken]
    );

    const selectCourse = useCallback(
        async (courseId) => {
            setSelectedCourseId(courseId);
            setTopics([]);
            setSelectedTitle("");
            setSelectedTopicId("");
            if (!courseId) return;
            setCatalogLoading(true);
            const res = await GetSegmentTopicsInfo(courseId, cmsToken);
            if (res && res.data && res.data.ResponseStatus === ResponseStatus.Success) {
                setTopics(res.data.ResponseData);
            } else {
                toast.error("Konu bilgileri alınamadı, lütfen tekrar deneyiniz");
            }
            setCatalogLoading(false);
        },
        [cmsToken]
    );

    const selectTitle = useCallback((title) => {
        setSelectedTitle(title);
        setSelectedTopicId("");
    }, []);

    const selectTopic = useCallback((topicId) => {
        setSelectedTopicId(topicId);
    }, []);

    const reset = useCallback(() => {
        setSelectedClassTypeId("");
        setSelectedSegmentId("");
        setCourses([]);
        setSelectedCourseId("");
        setTopics([]);
        setSelectedTitle("");
        setSelectedTopicId("");
    }, []);

    // Duplicate flow: resolve the cascade from an existing lecture's ids
    // (segment by ExamType, course by CourseId, topic by SubCourseTopicUniqueId).
    // Fills as deep as the ids still exist in the catalog; anything unmatched is
    // left for the operator to pick manually.
    const prefillFromLecture = useCallback(
        async (lecture) => {
            const segment = segments.find((s) => s.ExamTypeId == lecture.ExamType);
            if (!segment) {
                reset();
                return;
            }
            setSelectedClassTypeId(segment.ClassTypeId);
            setSelectedSegmentId(segment.Id);
            setCourses([]);
            setSelectedCourseId("");
            setTopics([]);
            setSelectedTitle("");
            setSelectedTopicId("");

            setCatalogLoading(true);
            const coursesRes = await GetSegmentCoursesInfo(segment.Id, cmsToken);
            if (!(coursesRes && coursesRes.data && coursesRes.data.ResponseStatus === ResponseStatus.Success)) {
                toast.error("Ders bilgileri alınamadı, lütfen tekrar deneyiniz");
                setCatalogLoading(false);
                return;
            }
            const courseList = coursesRes.data.ResponseData;
            setCourses(courseList);
            const course = courseList.find((c) => c.Id == lecture.CourseId);
            if (!course) {
                setCatalogLoading(false);
                return;
            }
            setSelectedCourseId(String(course.Id));

            const topicsRes = await GetSegmentTopicsInfo(course.Id, cmsToken);
            if (!(topicsRes && topicsRes.data && topicsRes.data.ResponseStatus === ResponseStatus.Success)) {
                toast.error("Konu bilgileri alınamadı, lütfen tekrar deneyiniz");
                setCatalogLoading(false);
                return;
            }
            const topicList = topicsRes.data.ResponseData;
            setTopics(topicList);
            const topic = topicList.find((t) => t.Id == lecture.SubCourseTopicUniqueId);
            if (topic) {
                setSelectedTitle(topic.Title);
                setSelectedTopicId(String(topic.Id));
            }
            setCatalogLoading(false);
        },
        [segments, cmsToken, reset]
    );

    // Derived values
    const classTypes = [];
    segments.forEach((s) => {
        if (!classTypes.some((c) => c.ClassTypeId === s.ClassTypeId)) {
            classTypes.push({
                ClassTypeId: s.ClassTypeId,
                // Rendered as plain YKS / LGS, matching the legacy radio labels
                label: s.ClassTypeId === CLASS_TYPE_YKS ? "YKS" : "LGS",
            });
        }
    });

    const segmentOptions = segments.filter((s) => s.ClassTypeId === selectedClassTypeId);
    const selectedSegment = segments.find((s) => s.Id == selectedSegmentId) || null;
    const selectedCourse = courses.find((c) => c.Id == selectedCourseId) || null;
    const titleOptions = [];
    topics.forEach((t) => {
        if (!titleOptions.includes(t.Title)) titleOptions.push(t.Title);
    });
    const subTopicOptions = topics.filter((t) => t.Title === selectedTitle);
    const selectedTopic = topics.find((t) => t.Id == selectedTopicId) || null;

    // Legacy ExamCategoryId radio value (1 = YKS, 2 = LGS) — payload contract
    const examCategoryRadioValue = EXAM_CATEGORY_ID_BY_CLASS_TYPE[selectedClassTypeId] || null;

    return {
        segments,
        classTypes,
        segmentOptions,
        courses,
        titleOptions,
        subTopicOptions,
        selectedClassTypeId,
        selectedSegmentId,
        selectedCourseId,
        selectedTitle,
        selectedTopicId,
        selectedSegment,
        selectedCourse,
        selectedTopic,
        examCategoryRadioValue,
        catalogLoading,
        selectClassType,
        selectSegment,
        selectCourse,
        selectTitle,
        selectTopic,
        reset,
        prefillFromLecture,
    };
};
